import React, { useRef, ChangeEvent, ClipboardEvent, useLayoutEffect, useEffect } from "react"
import cn from "classnames"

import { VERIFY_CODE_LENGTH, VERIFY_CODE_PLACEHOLDER } from "../../common"

import style from "./style.module.scss"

import { focusInput } from "utils/helpers"

const isNumbersOnly = (str: string) => /^\d*$/.test(str)

interface Props {
    code: string
    error?: boolean
    onChangeCode: (code: string) => void
}

export const VerificationCode = ({ code, error, onChangeCode }: Props) => {
    const inputRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
        if (inputRef.current) {
            focusInput(inputRef)
        }
    }, [])

    useEffect(() => {
        if (!inputRef.current) return

        const codeValue = code.replaceAll(VERIFY_CODE_PLACEHOLDER, "")
        const cursorPosition = codeValue.length

        inputRef.current.setSelectionRange(cursorPosition, cursorPosition)

        if (codeValue.length === VERIFY_CODE_LENGTH) {
            inputRef.current.blur()
        }
    }, [code])

    useLayoutEffect(() => {
        if (!inputRef.current) return

        const handleFocus = () => {
            if (code.replaceAll(VERIFY_CODE_PLACEHOLDER, "").length < VERIFY_CODE_LENGTH) {
                inputRef.current?.focus()
            }
        }

        inputRef.current.focus()
        inputRef.current.setSelectionRange(0, 0)
        inputRef.current.addEventListener("blur", handleFocus)

        return () => {
            inputRef.current?.removeEventListener("blur", handleFocus)
        }
    }, [code])

    const changeCode = (newCode: string) => {
        const trimmedCode = newCode.substring(0, VERIFY_CODE_LENGTH)
        const filledCode = trimmedCode.padEnd(VERIFY_CODE_LENGTH, VERIFY_CODE_PLACEHOLDER)
        onChangeCode(filledCode)
    }

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        const inputValue = event.target.value.replaceAll(VERIFY_CODE_PLACEHOLDER, "")
        if (!isNumbersOnly(inputValue)) return
        changeCode(inputValue)
    }

    const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
        event.preventDefault()
        const clipboardData = event.clipboardData.getData("text")
        const numericData = Array.from(clipboardData).filter(isNumbersOnly).join("")
        changeCode(numericData)
    }

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        const isControlKey =
            event.ctrlKey || event.metaKey || event.key === "Backspace" || event.key === "Delete"

        if (!isControlKey && !isNumbersOnly(event.key)) {
            event.preventDefault()
        }

        const currentVERIFY_CODE_LENGTH = code.replaceAll(VERIFY_CODE_PLACEHOLDER, "").length

        if (currentVERIFY_CODE_LENGTH >= VERIFY_CODE_LENGTH && !isControlKey) {
            event.preventDefault()
        }
    }

    return (
        <div className={style.code}>
            <input
                ref={inputRef}
                className={cn(style.codeInput, {
                    [style.error]: error,
                })}
                type="text"
                value={code}
                onChange={handleChange}
                onPaste={handlePaste}
                onKeyDown={handleKeyDown}
            />
        </div>
    )
}
